import React, { useState } from 'react';
import { Layers, Box, Download, Ruler } from 'lucide-react';
import EnterpriseSolutionModal from '../components/EnterpriseSolutionModal';

import PageMasthead from '../components/PageMasthead';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';

/**
 * CR3ATE DESKTOP — one of the three products launching at Intergeo.
 *
 * Copy comes from the product one-pager; nothing here is invented.
 *
 * Laid out to sit inside one screen on a desktop, the way /reco does, so the
 * offer is visible without scrolling: the page header is compact and shares its
 * row with the offer panel rather than standing above it, and the four
 * capabilities run as a single band beneath. It reflows to an ordinary scrolling
 * page on anything narrow, where fitting a screen is neither possible nor wanted.
 */

const CAPABILITIES = [
  {
    n: '01',
    label: 'Combine',
    Icon: Layers,
    body: 'Any camera — drone, phone, 360°, aerial and ground. Any scale, from a '
        + 'building to a city. One project, one model.',
  },
  {
    n: '02',
    label: 'Reconstruct',
    Icon: Box,
    body: 'Stills and video into high-fidelity 3D, with camera calibration, '
        + 'triangulation and bundle adjustment run for you.',
  },
  {
    n: '03',
    label: 'Deliver',
    Icon: Download,
    body: 'Reality meshes, point clouds and photorealistic Gaussian splats, '
        + 'built to hold up to engineering work.',
  },
  {
    n: '04',
    label: 'Measure',
    Icon: Ruler,
    body: 'Points, lines, areas and volumes, taken directly on the model you '
        + 'just built.',
  },
] as const;

const Cr3ateDesktopPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  return (
  <div className="flex min-h-dvh flex-col bg-ink-950 text-ink-100">
    <SiteHeader />

    <main className="relative flex-1">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(12,180,228,0.09),transparent_70%)]"
      />

      <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
        {/* The header and the offer share a row on wide screens. Stacked, the
            offer fell below the fold on a laptop, and it is the one thing on
            this page that has to be seen. */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-12">
          <div>
            <PageMasthead
              title="Cr3ate Desktop"
              tagline="Splat-native reconstruction on your own machine."
            />
            {/* The pitch, one step down from a display headline. It was set
                three steps larger and pushed the offer off a laptop screen. */}
            <p className="mt-6 text-balance font-brand text-xl font-light leading-snug tracking-tight text-ink-100 sm:text-2xl">
              Your images in.
              <br />
              Splat, mesh, point cloud out.
            </p>
            <p className="mt-4 max-w-xl text-balance font-brand text-body font-light leading-relaxed text-ink-300">
              The splat is a first class 3D model, not merely an export at the end.
            </p>
          </div>

          {/* The one boxed element on the sheet, and the only place the eye is
              asked to stop. The QR code becomes the button it pointed at. */}
          <div className="rounded-2xl border border-ember-500/40 bg-ember-500/[0.06] p-6 sm:p-7">
            <p className="mt-3 font-brand text-3xl font-light tracking-tight text-ink-100 sm:text-4xl">
              3 months free.
            </p>
            <p className="mt-3 text-body font-light text-ink-200">
              Then $950 per year, one machine.
            </p>
            <p className="mt-1 text-ui font-light text-ink-400">
              Ships Q4 2026 — sign up now.
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-crane-500 px-6 py-3 font-brand text-body font-medium text-ink-950 transition-colors hover:bg-crane-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 sm:w-auto"
            >
              Get 3 months free!
            </button>
          </div>
        </div>

        <div className="mt-10 border-t border-ink-800 pt-8 lg:mt-12">
          <h2 className="font-brand text-meta font-medium uppercase tracking-[0.18em] text-ember-500">
            What it does
          </h2>
          {/* Four across where there is room, two on a tablet, one on a phone —
              never three, so the numbering still reads in order. */}
          <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map(({ n, label, body, Icon }) => (
              <div key={n}>
                <div className="flex items-center gap-2.5">
                  <Icon className="h-5 w-5 flex-shrink-0 text-ember-500" aria-hidden />
                  <span className="font-brand text-meta font-medium uppercase tracking-[0.16em] text-ink-400">
                    {n}
                  </span>
                  <span className="font-brand text-title font-medium tracking-wide text-ink-100">
                    {label}
                  </span>
                </div>
                <p className="mt-2.5 text-ui font-light leading-relaxed text-ink-300">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>

    <SiteFooter />
    <EnterpriseSolutionModal
      isOpen={modalOpen}
      onClose={() => setModalOpen(false)}
      title="Try CR3ATE Desktop App"
      subtitle="Get 3 months free — tell us about your use case"
      source="desktop-app"
    />
  </div>
  );
};

export default Cr3ateDesktopPage;
